import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import ClientSearch from './ClientSearch';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { clientsApi, invoicesApi } from '../services/api';

const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

export const InvoiceFromDdtDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [ddts, setDdts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (open) clientsApi.getAll().then((r) => setClients(r.data)).catch(() => {}); }, [open]);

  useEffect(() => {
    setSelected([]); setDdts([]);
    if (!clientId) return;
    setLoading(true);
    invoicesApi.availableDdt(clientId).then((r) => setDdts(r.data))
      .catch(() => toast.error('Errore nel caricamento dei DDT')).finally(() => setLoading(false));
  }, [clientId]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const total = ddts.filter((d) => selected.includes(d.id)).reduce((s, d) => s + (d.total_amount || 0), 0);

  const create = async () => {
    setCreating(true);
    try {
      const r = await invoicesApi.fromDdt(selected, date);
      toast.success(`Fattura N. ${r.data.number} creata come bozza`);
      onOpenChange(false);
      navigate(`/fatture/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nella creazione'); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invoice-from-ddt-dialog" className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Fattura differita da DDT</DialogTitle>
          <DialogDescription>Scegli il cliente e i DDT non ancora fatturati da raggruppare in un'unica fattura.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <ClientSearch clients={clients} value={clientId} onChange={setClientId} />
          <div className="max-h-[40vh] overflow-y-auto border rounded-md divide-y">
            {loading ? <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
              : !clientId ? <p className="p-6 text-sm text-center text-muted-foreground">Seleziona un cliente</p>
              : ddts.length === 0 ? <p data-testid="no-available-ddt" className="p-6 text-sm text-center text-muted-foreground">Nessun DDT da fatturare per questo cliente</p>
              : ddts.map((d) => (
                <label key={d.id} data-testid={`available-ddt-${d.id}`} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40">
                  <Checkbox data-testid={`select-ddt-${d.id}`} checked={selected.includes(d.id)} onCheckedChange={() => toggle(d.id)} />
                  <span className="font-['JetBrains_Mono'] text-sm font-semibold w-24">N. {d.number}/{d.year}</span>
                  <span className="text-sm text-muted-foreground flex-1">{new Date(d.date).toLocaleDateString('it-IT')}</span>
                  <span className="font-['JetBrains_Mono'] text-sm">{eur(d.total_amount)}</span>
                </label>
              ))}
          </div>
          <div className="flex items-end justify-between gap-4">
            <div className="space-y-1">
              <Label className="label-uppercase">Data fattura</Label>
              <Input data-testid="invoice-from-ddt-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44" />
            </div>
            <div className="text-sm text-right">
              <div className="text-muted-foreground">{selected.length} DDT selezionati</div>
              <div data-testid="invoice-from-ddt-total" className="font-bold font-['JetBrains_Mono']">{eur(total)}</div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="create-invoice-from-ddt-btn" className="btn-primary" disabled={!selected.length || creating} onClick={create}>
            {creating ? 'Creazione...' : 'Crea fattura'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceFromDdtDialog;
